// Mesajlar › Yeni kişiler (whatsapp:contacts) — iki bölme: kişiler | konu.
// URL: ?gorunum=yeni-kisiler&kisi=<contactId>

import React, { useEffect, useMemo, useState } from "react";
import useDebouncedSearch from "../../hooks/useDebouncedSearch";
import { Box } from "@mui/material";
import { WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import { EmptyState } from "../../components/atoms";
import { useAlert } from "../../context/AlertProvider";
import { COLORS } from "../../styles/uiConstants";
import {
  useLinkWhatsAppContactMutation,
  useListWhatsAppContactsQuery,
  useMarkWhatsAppContactReadMutation,
  useOlderWhatsAppContactMessagesMutation,
  useSendWhatsAppContactMessageMutation,
  useWhatsAppContactThreadQuery,
} from "../../store/whatsappApi";
import ContactList from "./ContactList";
import ContactThread from "./ContactThread";
import LinkPatientDialog from "./LinkPatientDialog";

const LIST_LIMIT = 100;
const THREAD_LIMIT = 40;

/**
 * @param {string} token
 * @param {string|null} contactId - URL'deki seçili kişi
 * @param {(id: string|null) => void} onSelectContact
 * @param {(res: {conversationId, patientId, phoneMismatch}) => void} onLinked
 * @param {number} pollingInterval
 * @param {boolean} visible - sekme görünür mü (okundu işareti için)
 * @param {boolean} narrow - dar ekran (tek bölme)
 * @param {React.ReactNode} switcher - liste üstündeki Hastalar / Yeni kişiler seçimi
 * @param {object} palette
 */
export default function ContactsView({
  token,
  contactId,
  onSelectContact,
  onLinked,
  pollingInterval,
  visible,
  narrow,
  switcher,
  palette,
}) {
  const { showError } = useAlert();
  const [search, setSearch] = useState("");
  const { debounced, pending: searchPending } = useDebouncedSearch(search);

  // currentData: arama değişince yeni sonuç gelene kadar boş — eski liste görünmez.
  // isFetching tek başına kullanılmaz; yoksa her yoklamada (polling) iskelet yanıp söner.
  const { currentData: listRes, isFetching: listFetching } = useListWhatsAppContactsQuery(
    { token, search: debounced.trim() || undefined, page: 1, limit: LIST_LIMIT },
    { skip: !token, pollingInterval }
  );
  const contacts = useMemo(() => listRes?.data?.contacts || [], [listRes]);

  const {
    currentData: threadRes,
    isLoading: threadLoading,
    isFetching: threadFetching,
    isError: threadError,
  } = useWhatsAppContactThreadQuery(
    { token, contactId, limit: THREAD_LIMIT },
    { skip: !token || !contactId, pollingInterval }
  );
  const thread = contactId ? threadRes?.data || null : null;

  // ── Eski sayfalar ──
  const [older, setOlder] = useState([]);
  const [olderHasMore, setOlderHasMore] = useState(null);
  const [fetchOlder, { isLoading: loadingOlder }] = useOlderWhatsAppContactMessagesMutation();
  useEffect(() => {
    setOlder([]);
    setOlderHasMore(null);
  }, [contactId]);
  const hasMoreOlder = olderHasMore === null ? Boolean(thread?.hasMore) : olderHasMore;
  const loadOlder = async () => {
    const first = older[0] || thread?.messages?.[0];
    if (!first || !contactId) return;
    try {
      const res = await fetchOlder({ token, contactId, before: first.createdAt, limit: THREAD_LIMIT }).unwrap();
      setOlder((prev) => [...(res?.data?.messages || []), ...prev]);
      setOlderHasMore(Boolean(res?.data?.hasMore));
    } catch (err) {
      showError(err?.data?.message || "Eski mesajlar yüklenemedi");
    }
  };

  // ── Okundu ──
  const [markRead] = useMarkWhatsAppContactReadMutation();
  const unread = thread?.contact?.unread || 0;
  const lastAt = thread?.contact?.lastMessageAt;
  useEffect(() => {
    if (!contactId || unread <= 0 || !visible) return;
    markRead({ token, contactId }).catch(() => {});
  }, [contactId, unread, lastAt, visible, token, markRead]);

  // ── Gönder ──
  const [send, { isLoading: sending }] = useSendWhatsAppContactMessageMutation();
  const handleSend = async (text) => {
    try {
      await send({ token, contactId, text }).unwrap();
      return true;
    } catch (err) {
      showError(err?.data?.message || "Mesaj gönderilemedi");
      return false;
    }
  };

  // ── Hastaya bağla ──
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, { isLoading: linking }] = useLinkWhatsAppContactMutation();
  const handleLink = async (patientId) => {
    try {
      const res = await link({ token, contactId, patientId }).unwrap();
      setLinkOpen(false);
      onLinked(res?.data || { patientId });
    } catch (err) {
      showError(err?.data?.message || "Kişi bağlanamadı");
    }
  };

  const showList = !narrow || !contactId;
  const showThread = !narrow || Boolean(contactId);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "340px 1fr", lg: "380px 1fr" },
      }}
    >
      {showList ? (
        <Box sx={{ minHeight: 0, display: "flex", flexDirection: "column", borderRight: { md: `1px solid ${COLORS.slate200}` } }}>
          {switcher}
          <ContactList
            contacts={contacts}
            loading={searchPending || (listFetching && !listRes)}
            selectedId={contactId}
            onSelect={(c) => onSelectContact(c._id)}
            search={search}
            onSearchChange={setSearch}
            total={listRes?.data?.total || 0}
            palette={palette}
          />
        </Box>
      ) : null}
      {showThread ? (
        <Box sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
          {contactId ? (
            <ContactThread
              thread={thread}
              loading={threadLoading || (threadFetching && !thread)}
              error={threadError}
              olderMessages={older}
              hasMoreOlder={hasMoreOlder}
              loadingOlder={loadingOlder}
              onLoadOlder={loadOlder}
              onSend={handleSend}
              sending={sending}
              onLink={() => setLinkOpen(true)}
              onBack={narrow ? () => onSelectContact(null) : undefined}
              palette={palette}
            />
          ) : (
            <EmptyState
              variant="illustrated"
              icon={<WhatsAppIcon />}
              title="Bir kişi seçin"
              description="Soldaki listeden bir numara seçerek WhatsApp yazışmasını görüntüleyin."
              sx={{ flex: 1 }}
            />
          )}
        </Box>
      ) : null}
      <LinkPatientDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        contact={thread?.contact}
        onLink={handleLink}
        linking={linking}
        token={token}
        palette={palette}
      />
    </Box>
  );
}
